import React, { useId, useMemo } from "react";
import { Box, BoxProps } from "@mui/material";
import { colors } from "../../styles/theme";
import { LOGO_WORDMARK_VIEWBOX, LogoWordmarkPaths } from "../Icons/Icons";

export type AnimatedLogoProps = BoxProps & {
  progress?: number;
  animationTime?: number;
  color?: string;
  dimOpacity?: number;
};

const [, , VIEW_W, VIEW_H] = LOGO_WORDMARK_VIEWBOX.split(" ").map(Number);

export const AnimatedLogo: React.FC<AnimatedLogoProps> = (
  {
    progress = 0,
    animationTime = 300,
    color = colors.text.v4,
    dimOpacity = 0.16,
    ...rest
  }
) => {
  const rid = useId();
  const shapeId = useMemo(() => `a-shape-${rid}`, [rid]);
  const maskId = useMemo(() => `a-mask-${rid}`, [rid]);

  const p = Math.max(0, Math.min(1, progress));
  const fillH = VIEW_H * p;
  const fillY = VIEW_H - fillH;

  return (
    <Box
      component={'svg'}
      viewBox={LOGO_WORDMARK_VIEWBOX}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      {...rest}
    >
      <defs>
        {/* Wordmark shape, referenced twice with different fills */}
        <g id={shapeId}>
          <LogoWordmarkPaths />
        </g>

        {/* Progress mask: reveals only the bottom-to-top portion */}
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width={VIEW_W} height={VIEW_H}>
          <rect width={VIEW_W} height={VIEW_H} fill="#000" />
          <rect
            x="0"
            y={fillY}
            width={VIEW_W}
            height={fillH}
            fill="#fff"
            style={{ transition: `y ${animationTime}ms ease, height ${animationTime}ms ease` }}
          />
        </mask>
      </defs>

      {/* Dimmed, not-yet-filled wordmark */}
      <use href={`#${shapeId}`} fill={color} opacity={dimOpacity} />

      {/* Solid progress fill rising from the bottom */}
      <use href={`#${shapeId}`} fill={color} mask={`url(#${maskId})`} />
    </Box>
  );
};
